import React, { useState, useEffect } from 'react';
import TranslationSimulator from './TranslationSimulator';
import './App.css';

function App() {
  const [apkInfo, setApkInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentScreenshot, setCurrentScreenshot] = useState(0);

  // Screenshots data
  const screenshots = [
    { src: '/screenshots/1.png', alt: '深语翻译应用界面截图', caption: '主界面 - 便捷的翻译操作面板' },
    { src: '/screenshots/2.png', alt: '深语翻译应用界面截图', caption: '文本输入' },
    { src: '/screenshots/3.png', alt: '深语翻译应用界面截图', caption: '翻译结果展示' }
  ];

  useEffect(() => {
    // Fetch the APK information from the JSON file
    fetch('/downloads.json')
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to fetch APK information');
        }
        return response.json();
      })
      .then(data => {
        setApkInfo(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  // Function to get the latest APK for a specific architecture
  const getLatestApk = (arch) => {
    if (!apkInfo || !apkInfo.apks) return null;
    return apkInfo.apks.find(apk => 
      apk.version === apkInfo.latestVersion && apk.arch === arch
    );
  };

  // Get the latest APKs for both architectures
  const latestArm64 = getLatestApk('arm64-v8a');
  const latestArmv7 = getLatestApk('armeabi-v7a');

  // Function to close menu when clicking a link
  const handleLinkClick = () => {
    setIsMenuOpen(false);
  };

  // Navigation functions for screenshots
  const nextScreenshot = () => {
    setCurrentScreenshot((prev) => (prev === screenshots.length - 1 ? 0 : prev + 1));
  };

  const prevScreenshot = () => {
    setCurrentScreenshot((prev) => (prev === 0 ? screenshots.length - 1 : prev - 1));
  };

  return (
    <div className="App">
      {/* Header Section */}
      <header className="hero-section">
        <div className="container">
          <div className="header-nav">
            <div className="logo">
              <div className="logo-content">
                <img src="/logo192.png" alt="深语翻译 Logo" className="app-logo" />
                <div className="logo-text">
                  <h1 className="main-title">深语翻译</h1>
                  <h2 className="main-subtitle">Synodora Translate</h2>
                </div>
              </div>
            </div>
            {/* Mobile menu button - show on mobile screens */}
            <div className="menu-toggle" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              <div className={`hamburger ${isMenuOpen ? 'active' : ''}`}></div>
            </div>
            {/* Desktop Navigation - show on larger screens */}
            <nav className="main-nav desktop-nav">
              <a href="#screenshots" onClick={handleLinkClick}>界面预览</a>
              <a href="#features" onClick={handleLinkClick}>功能特色</a>
              <a href="#engines" onClick={handleLinkClick}>翻译引擎</a>
              <a href="#solutions" onClick={handleLinkClick}>解决方案</a>
              <a href="#use-cases" onClick={handleLinkClick}>应用场景</a>
            </nav>
          </div>
          {/* Mobile Navigation - show when menu is open */}
          <nav className={`main-nav mobile-nav ${isMenuOpen ? 'active' : ''}`}>
            <a href="#screenshots" onClick={handleLinkClick}>界面预览</a>
            <a href="#features" onClick={handleLinkClick}>功能特色</a>
            <a href="#engines" onClick={handleLinkClick}>翻译引擎</a>
            <a href="#solutions" onClick={handleLinkClick}>解决方案</a>
            <a href="#use-cases" onClick={handleLinkClick}>应用场景</a>
          </nav>
          <p className="subtitle">以专业AI翻译，定义沟通新境界</p>
          <p className="tagline"><span className="highlight-text">环球交流，深译相通</span></p>
          
          {loading ? (
            <p className="loading-text">正在加载下载信息...</p>
          ) : error ? (
            <p className="error-text">加载失败: {error}</p>
          ) : (
            <div className="download-section">
              <div className="download-buttons">
                {latestArm64 && (
                  <a
                    className="download-button"
                    href={`/download/${latestArm64.fileName}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    下载APK (64位) v{latestArm64.version}
                  </a>
                )}
                
                {latestArmv7 && (
                  <a
                    className="download-button secondary"
                    href={`/download/${latestArmv7.fileName}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    下载APK (32位) v{latestArmv7.version}
                  </a>
                )}
              </div>
              
              {apkInfo && (
                <>
                  <p className="daily-limit">
                    当前版本: v{apkInfo.latestVersion} | 
                    发布日期: {apkInfo.releaseDate}
                  </p>
                  <p className="daily-limit">
                    每天免费畅享10000个字符的专业AI翻译！
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Screenshots Section */}
      <section id="screenshots" className="screenshots-section">
        <div className="container">
          <h2 className="section-title">应用界面预览</h2>
          <h3 className="section-subtitle">App Interface Preview</h3>
          
          <div className="screenshots-container">
            <button className="screenshot-nav prev" onClick={prevScreenshot}>
              &#8249;
            </button>
            
            <div className="screenshot-item">
              <div className="screenshot-container">
                <img 
                  src={screenshots[currentScreenshot].src} 
                  alt={screenshots[currentScreenshot].alt} 
                  className="screenshot-image" 
                />
              </div>
              <p className="screenshot-caption">{screenshots[currentScreenshot].caption}</p>
            </div>
            
            <button className="screenshot-nav next" onClick={nextScreenshot}>
              &#8250;
            </button>
          </div>
          
          <div className="screenshot-indicators">
            {screenshots.map((_, index) => (
              <span 
                key={index} 
                className={`indicator ${index === currentScreenshot ? 'active' : ''}`}
                onClick={() => setCurrentScreenshot(index)}
              ></span>
            ))}
          </div>
        </div>
      </section>

      {/* Translation Simulator Section */}
      <section className="globe-section">
        <div className="container">
          <TranslationSimulator />
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="container">
          <h2 className="section-title">为什么选择深语翻译？</h2>
          <h3 className="section-subtitle">Why Synodora Translate?</h3>
          <div className="features-grid">
            <div className="feature-card">
              <h3>200+语言全覆盖</h3>
              <p>全球主流语言、稀缺语言一网打尽，沟通无国界！</p>
            </div>
            <div className="feature-card">
              <h3>实时同声传译</h3>
              <p>会议、演讲、对话同步翻译，十余种语言混合输入无障碍，沟通如行云流水！</p>
            </div>
            <div className="feature-card">
              <h3>专业文档翻译神器</h3>
              <p>完美处理Word、PDF等格式！深度锁定专业术语（法律、金融、医疗、科技等），格式精准还原。</p>
            </div>
            <div className="feature-card">
              <h3>智能图片OCR翻译</h3>
              <p>对准菜单、路牌、说明书、海报等一拍即译！文字识别精准，版式还原清晰。</p>
            </div>
            <div className="feature-card">
              <h3>真人级语音合成</h3>
              <p>基于深度神经网络，输出媲美真人、富有情感的语音，听得懂，更听得舒服！</p>
            </div>
            <div className="feature-card">
              <h3>专业术语深度定制</h3>
              <p>针对垂直领域（如医学、工程、法律），可定制专属术语库，确保翻译准确性与专业性。</p>
            </div>
          </div>
        </div>
      </section>

      {/* Engine Section */}
      <section id="engines" className="engine-section">
        <div className="container">
          <h2 className="section-title">深译引擎体系</h2>
          <h3 className="section-subtitle">Deep Translation Engine System</h3>
          <p className="section-description">基于全球领先的深度神经网络架构，构建三大「深译引擎」，让每次翻译直达本质：</p>
          
          <div className="engines-container">
            <div className="engine-card">
              <h3>AI深译引擎</h3>
              <p>语境理解专家——上下文感知式翻译，深度解析、深度改进。</p>
            </div>
            <div className="engine-card">
              <h3>标准深译引擎</h3>
              <p>通用场景能手——双语逐句对照输出，快速翻译匹配日常需求。</p>
            </div>
            <div className="engine-card">
              <h3>专业深译引擎</h3>
              <p>垂直领域大师——翻译置信度智能评分系统，垂直领域术语库深度定制。</p>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section id="solutions" className="solutions-section">
        <div className="container">
          <h2 className="section-title">全场景解决方案</h2>
          <h3 className="section-subtitle">Full-Scenario Solutions</h3>
          <p className="section-description">从咖啡馆到国际会议厅，深语翻译让每次「环球交流」都「深译相通」：</p>
          
          <div className="solutions-grid">
            <div className="solution-card">
              <h3>AI深译<span className="parentheses-content">（AI翻译）</span></h3>
              <p>每日10000字符免费额度，大模型驱动语境理解。</p>
            </div>
            <div className="solution-card">
              <h3>语音深译<span className="parentheses-content">（语音翻译|对话翻译）</span></h3>
              <p>声临其境，基于深度神经网络的TTS技术，媲美真人语音的情感化输出。</p>
            </div>
            <div className="solution-card">
              <h3>同传深译<span className="parentheses-content">（同传翻译|同声传译）</span></h3>
              <p>实时流式同声传译，十余种语言无障碍混合输入。</p>
            </div>
            <div className="solution-card">
              <h3>文档深译<span className="parentheses-content">（文档翻译|PDF翻译|Word翻译）</span></h3>
              <p>完美保留Word/PDF格式，专业术语深度锁定。</p>
            </div>
            <div className="solution-card">
              <h3>图像深译<span className="parentheses-content">（图片翻译|拍照翻译|OCR翻译）</span></h3>
              <p>OCR+版式还原技术，外文菜单路牌即扫即懂。</p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="use-cases" className="use-cases-section">
        <div className="container">
          <h2 className="section-title">满足您全场景翻译需求</h2>
          <h3 className="section-subtitle">Meeting Your Full-Scenario Translation Needs</h3>
          
          <div className="use-cases-grid">
            <div className="use-case">
              <h3>出国旅游</h3>
              <p>菜单路牌即拍即译，问路购物轻松搞定。</p>
            </div>
            <div className="use-case">
              <h3>商务会议</h3>
              <p>实时同传打破语言障碍，谈判签约精准无误（专业文档翻译+术语库）。</p>
            </div>
            <div className="use-case">
              <h3>学术科研</h3>
              <p>高效翻译外文文献（文档翻译），专业术语精准传达，论文写作好帮手。</p>
            </div>
            <div className="use-case">
              <h3>语言学习</h3>
              <p>双语对照学习（标准深译引擎），真人发音辅助练习。</p>
            </div>
            <div className="use-case">
              <h3>日常交流</h3>
              <p>与外国朋友聊天、浏览外网资讯，快速翻译不求人。</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p className="footer-tagline"><span className="highlight-text">环球交流，深译相通</span></p>
          <p className="footer-name">深语翻译 — 您值得托付的深度语言解决方案</p>
          <p className="footer-subname">Synodora Translate - Your Trusted Deep Language Solution</p>
          <p className="registration-info">
            <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">
              闽ICP备2024068749号-3
            </a>
            <br />
            <span className="public-security-registration">
              <img src="/beian.png" alt="Public Security Registration" className="beian-icon" />
              <a href="https://beian.mps.gov.cn/#/query/webSearch?code=35050302001208" rel="noreferrer" target="_blank">闽公网安备35050302001208号</a>
            </span>
          </p>
          <p className="copyright">©2024-{new Date().getFullYear()} 泉州深语科技有限公司 版权所有</p>
        </div>
      </footer>
    </div>
  );
}

export default App;